import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { fetchKleedTijdlijn, type KleedTijdlijnResult, type TijdlijnStap } from '../../lib/kleed-tijdlijn';

interface Props {
  woId?: string;
  itemId?: string;
  compact?: boolean;
  /** Wordt aangeroepen zodra de data binnen is (bv. om te weten of er stappen zijn). */
  onLoaded?: (result: KleedTijdlijnResult) => void;
}

const AFKEUR_FASES = new Set(['nabehandelen', 'nogmaals-reinigen']);

function momentKort(iso: string): { dag: string; tijd: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { dag: iso, tijd: '' };
  const dag = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'short' }).format(d);
  const tijd = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return { dag, tijd: tijd === '00:00' ? '' : tijd };
}

function bronLabel(bron: TijdlijnStap['bron']): string | null {
  if (bron === 'tekst') return 'uit wasrapport';
  if (bron === 'afgeleid') return 'afgeleid';
  return null;
}

/**
 * Verticale fase-tijdlijn van een kleed (of alle kleden van een order),
 * oudste boven. Alleen weergave: de stappen komen kant-en-klaar uit de lezer.
 */
export function KleedTijdlijn({ woId, itemId, compact = false, onLoaded }: Props) {
  const [data, setData] = useState<KleedTijdlijnResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!woId && !itemId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = itemId ? { itemId } : { woId: woId as string };
    fetchKleedTijdlijn(params)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        onLoaded?.(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Tijdlijn laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId, itemId]);

  if (loading) return <p className="text-xs text-slate-400">Tijdlijn laden…</p>;
  if (error) return <p className="text-xs text-red-600">{error}</p>;
  if (!data || data.kleden.length === 0) return <p className="text-sm text-slate-400">Nog geen stappen</p>;

  const meerdere = data.kleden.length > 1;
  return (
    <div className="space-y-4">
      {data.kleden.map((k) => (
        <div key={k.itemId}>
          {meerdere && (
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-xs font-medium text-slate-700">{k.ref || k.itemId}</span>
              <span className="text-xs text-slate-500">{k.type}</span>
              {k.huidigeFase && <span className="text-xs text-indigo-700">{k.huidigeFase}</span>}
            </div>
          )}
          <TijdlijnLijst stappen={k.tijdlijn} compact={compact} />
        </div>
      ))}
    </div>
  );
}

export function TijdlijnLijst({ stappen, compact = false }: { stappen: TijdlijnStap[]; compact?: boolean }) {
  if (stappen.length === 0) return <p className="text-sm text-slate-400">Nog geen stappen</p>;
  return (
    <ol className="relative border-l border-slate-200 ml-1.5 space-y-2" aria-label="Tijdlijn van dit kleed">
      {stappen.map((s, i) => {
        const gedempt = s.bron === 'tekst' || s.bron === 'afgeleid';
        const afkeur = AFKEUR_FASES.has(String(s.fase)) || !!s.reden;
        const { dag, tijd } = momentKort(s.moment);
        const bron = bronLabel(s.bron);
        return (
          <li key={`${s.moment}-${s.fase}-${i}`} className="pl-3 relative">
            <span
              className={clsx(
                'absolute -left-[5px] top-1.5 w-2 h-2 rounded-full border',
                afkeur ? 'bg-orange-400 border-orange-400' : gedempt ? 'bg-white border-slate-300' : 'bg-indigo-500 border-indigo-500'
              )}
              aria-hidden
            />
            <div className={clsx('flex flex-wrap items-baseline gap-x-2 gap-y-0.5', compact ? 'text-xs' : 'text-sm')}>
              <span className="text-slate-400 tabular-nums whitespace-nowrap text-xs">
                {dag}{tijd ? ` ${tijd}` : ''}
              </span>
              <span className={clsx(gedempt ? 'text-slate-500' : 'text-slate-800', afkeur && 'text-orange-800')}>{s.label}</span>
              {s.methode && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{s.methode}</span>
              )}
              {s.reden && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-800">{s.reden}</span>
              )}
              {s.medewerker && <span className="text-xs text-slate-400">door {s.medewerker}</span>}
              {typeof s.fotos === 'number' && s.fotos > 0 && (
                <span className="text-xs text-slate-400">{s.fotos} foto{s.fotos === 1 ? '' : "'s"}</span>
              )}
              {bron && <span className="text-[10px] text-slate-400 italic">{bron}</span>}
            </div>
            {s.notitie && (
              <p className={clsx('text-xs text-slate-500 mt-0.5 whitespace-pre-wrap', compact && 'line-clamp-2')}>{s.notitie}</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
